import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { TOWER } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { Radar, ShieldAlert, AlertTriangle, Info, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BAND_STYLE = {
  advanced: "bg-forest-mid text-white border-forest-dark",
  strong: "bg-emerald-100 text-emerald-900 border-emerald-300",
  developing: "bg-amber-100 text-amber-900 border-amber-300",
  early_progress: "bg-orange-100 text-orange-900 border-orange-300",
  priority_improvement: "bg-rose-100 text-rose-900 border-rose-300",
  restricted: "bg-rose-600 text-white border-rose-800",
};
const fmt1 = (v) => (v == null ? "—" : Number(v).toFixed(1));

function Chip({ children, className = "", ...p }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${className}`} {...p}>{children}</span>;
}

function Sparkline({ points }) {
  const vals = points.filter((p) => p.overall != null);
  if (vals.length === 0) return <span className="text-xs text-muted-foreground">no issued runs</span>;
  const w = 128, h = 34, pad = 3;
  const xs = (i) => pad + (points.length <= 1 ? 0 : (i * (w - 2 * pad)) / (points.length - 1));
  const ys = (v) => h - pad - ((v / 100) * (h - 2 * pad));
  const line = points.map((p, i) => (p.overall == null ? null : `${xs(i)},${ys(p.overall)}`)).filter(Boolean).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={line} fill="none" stroke="#2f6b4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => p.overall == null ? null : (
        <g key={i}>
          <circle cx={xs(i)} cy={ys(p.overall)} r={p.method_break ? 3.5 : 2.5}
                  fill={p.method_break ? "#f59e0b" : "#2f6b4f"} stroke="white" strokeWidth="1" />
          {p.method_break && <title>Method break at {p.label} (profile changed)</title>}
        </g>
      ))}
    </svg>
  );
}

export default function PortfolioTowerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async (period) => {
    setLoading(true);
    try {
      const r = await api.get(`/score/portfolio${period ? `?period=${encodeURIComponent(period)}` : ""}`);
      setData(r.data);
      setSel(r.data.selected || "");
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(apiErr(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const changePeriod = (v) => { setSel(v); load(v); };

  if (forbidden) return (
    <div className="flex min-h-[50vh] items-center justify-center" data-testid={TOWER.root}>
      <Card className="max-w-sm border-sage-light p-8 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-rose-500" />
        <p className="mt-3 font-heading text-lg font-semibold text-forest-dark">Not authorised</p>
        <p className="mt-1 text-sm text-muted-foreground">The Control Tower is for internal portfolio roles. Ask your administrator if you need access.</p>
      </Card>
    </div>
  );
  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading Control Tower…</p>;

  const agg = data?.aggregates;
  const noData = !data?.periods?.length;

  return (
    <div className="space-y-5" data-testid={TOWER.root}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark"><Radar className="h-6 w-6 text-forest-mid" /> Control Tower</h1>
          <p className="text-sm text-muted-foreground">Cross-contract portfolio of <b>issued</b> ROOTS scores · comparability-guarded</p>
        </div>
        {data?.periods?.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Comparability key</span>
            <Select value={sel} onValueChange={changePeriod}>
              <SelectTrigger className="h-9 w-[160px]" data-testid={TOWER.periodSelect}><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                {data.periods.map((p) => <SelectItem key={p.label} value={p.label}>{p.label} · {p.count} issued</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {noData ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">
          No issued ROOTS scores yet. Issue an assessment from a contract's ROOTS Score workspace to populate the Control Tower.
        </Card>
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-lg border-l-4 border-forest-mid bg-sage-surface p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-forest-mid" />
            <p className="text-xs text-forest-dark">Comparing like with like: only contracts issued for <b>{sel}</b> on the same profile (<span className="font-mono">{data.comparability_profile || "—"}</span>) are averaged and ranked. Different-method results are listed separately as method breaks and never blended.</p>
          </div>

          {/* aggregate tiles */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={TOWER.avgTile}>
            <Card className="border-sage-light p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contracts</p>
              <p className="mt-1 font-heading text-3xl font-bold text-forest-dark">{agg?.count ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">comparable for {sel}</p>
            </Card>
            <Card className="border-sage-light p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Avg overall</p>
              <p className="mt-1 font-heading text-3xl font-bold text-forest-dark">{agg ? fmt1(agg.avg_overall) : "—"}</p>
              <p className="text-[11px] text-muted-foreground">/ 100</p>
            </Card>
            {agg && Object.entries(data.pillar_labels).slice(0, 2).map(([k, label]) => (
              <Card key={k} className="border-sage-light p-4">
                <p className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className="mt-1 font-heading text-3xl font-bold text-forest-dark">{fmt1(agg.pillars[k])}</p>
                <p className="text-[11px] text-muted-foreground">pillar avg</p>
              </Card>
            ))}
          </div>

          {/* band distribution */}
          {agg && (
            <Card className="border-sage-light p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Band distribution · {sel}</p>
              <div className="flex flex-wrap gap-2">
                {agg.bands.map((b) => (
                  <Chip key={b.label} className={BAND_STYLE[b.key] || "bg-slate-100 text-slate-700 border-slate-200"}>{b.label}: {b.count}</Chip>
                ))}
              </div>
            </Card>
          )}

          {/* comparison table */}
          <Card className="border-sage-light p-0">
            <div className="border-b border-sage-light p-4"><h3 className="font-heading text-base font-semibold text-forest-dark">Comparison · {sel}</h3></div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contract</TableHead><TableHead>Overall</TableHead><TableHead>Band</TableHead>
                    {Object.values(data.pillar_labels).map((l) => <TableHead key={l} className="hidden md:table-cell">{l.slice(0, 4)}</TableHead>)}
                    <TableHead className="hidden sm:table-cell">C</TableHead><TableHead className="hidden sm:table-cell">Q</TableHead><TableHead>Issued</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r) => (
                    <TableRow key={r.contract_id} data-testid={TOWER.row(r.contract_id)} className="cursor-pointer hover:bg-sage-surface/50" onClick={() => navigate("/project")}>
                      <TableCell className="font-medium text-forest-dark">{r.contract_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-heading text-lg font-bold text-forest-dark">{fmt1(r.overall_display)}</span>
                          <div className="hidden h-1.5 w-16 rounded-full bg-slate-100 sm:block"><div className="h-1.5 rounded-full bg-forest-mid" style={{ width: `${Math.min(100, r.overall_display || 0)}%` }} /></div>
                        </div>
                      </TableCell>
                      <TableCell><Chip className={BAND_STYLE[r.band_key] || "bg-slate-100 text-slate-700 border-slate-200"}>{r.gate === "restricted" && <ShieldAlert className="h-3 w-3" />}{r.band_label}</Chip></TableCell>
                      {Object.keys(data.pillar_labels).map((k) => <TableCell key={k} className="hidden text-sm md:table-cell">{fmt1(r.pillars[k])}</TableCell>)}
                      <TableCell className="hidden text-sm sm:table-cell">{fmt1(r.coverage_display)}%</TableCell>
                      <TableCell className="hidden text-sm sm:table-cell">{r.evidence_index == null ? "—" : fmt1(r.evidence_index)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(r.issued_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* method breaks */}
          {data.incomparable?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/60 p-4">
              <p className="flex items-center gap-1 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Method breaks — not comparable for {sel}</p>
              <p className="mt-0.5 text-[11px] text-amber-900">Issued on a different profile version; excluded from averages and ranking.</p>
              <div className="mt-2 space-y-1">
                {data.incomparable.map((r) => (
                  <div key={r.contract_id} className="flex items-center justify-between rounded border border-amber-200 bg-white p-2 text-xs">
                    <span className="font-medium text-forest-dark">{r.contract_name} — {fmt1(r.overall_display)} {r.band_label}</span>
                    <span className="font-mono text-amber-700">{r.profile_version}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* trends */}
          <Card className="border-sage-light p-4">
            <p className="mb-3 flex items-center gap-1.5 font-heading text-base font-semibold text-forest-dark"><TrendingUp className="h-4 w-4 text-forest-mid" /> Pillar & score trends <span className="text-xs font-normal text-muted-foreground">— issued runs over time</span></p>
            <div className="space-y-2">
              {data.trends.map((t) => (
                <div key={t.contract_id} className="flex items-center justify-between gap-3 rounded-lg border border-sage-light p-2.5" data-testid={TOWER.trendRow(t.contract_id)}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-forest-dark">{t.contract_name}</p>
                    <p className="text-[11px] text-muted-foreground">{t.points.filter((p) => p.overall != null).length} issued · latest {fmt1(t.points[t.points.length - 1]?.overall)}{t.points.some((p) => p.method_break) && <span className="ml-1 text-amber-700">· method break</span>}</p>
                  </div>
                  <Sparkline points={t.points} />
                </div>
              ))}
            </div>
          </Card>

          {/* not assessed */}
          {data.not_assessed?.length > 0 && (
            <Card className="border-sage-light p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">No issued assessment yet</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.not_assessed.map((c) => <Chip key={c.contract_id} className="bg-slate-50 text-slate-600 border-slate-200">{c.contract_name}</Chip>)}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
